import { Controller } from "@hotwired/stimulus"

// Lazy-loads video sources after poster image paints for better LCP
export default class extends Controller {
  static targets = ["video"]

  connect() {
    // Wait for poster to paint before loading video sources
    // This ensures the poster becomes the LCP, not the video
    this.loadVideoAfterPosterPaints()

    // Listen for Turbo events to restart video
    this.boundPlayVideo = this.playVideo.bind(this)
    document.addEventListener("turbo:load", this.boundPlayVideo)
    document.addEventListener("turbo:render", this.boundPlayVideo)
  }

  disconnect() {
    document.removeEventListener("turbo:load", this.boundPlayVideo)
    document.removeEventListener("turbo:render", this.boundPlayVideo)
  }

  loadVideoAfterPosterPaints() {
    if (!this.hasVideoTarget) return

    // Use requestAnimationFrame to wait for the next paint cycle
    // This ensures the poster image has been rendered to the screen
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        // Small additional delay to ensure poster is fully painted
        // and prioritized by the browser as LCP
        setTimeout(() => {
          this.addVideoSources()
        }, 100)
      })
    })
  }

  // Get video source URLs from data attributes on the video element
  getDesktopSrc() {
    return this.videoTarget.dataset.videoAutoplayDesktopSrcValue
  }

  getMobileSrc() {
    return this.videoTarget.dataset.videoAutoplayMobileSrcValue
  }

  addVideoSources() {
    if (!this.hasVideoTarget) return

    const video = this.videoTarget
    const isMobile = window.innerWidth <= 768
    const desktopSrc = this.getDesktopSrc()
    const mobileSrc = this.getMobileSrc()

    // Add appropriate video source based on viewport
    if (isMobile && mobileSrc) {
      const mobileSource = document.createElement('source')
      mobileSource.src = mobileSrc
      mobileSource.type = 'video/mp4'
      mobileSource.media = '(max-width: 768px)'
      video.appendChild(mobileSource)
    }

    if (desktopSrc) {
      const desktopSource = document.createElement('source')
      desktopSource.src = desktopSrc
      desktopSource.type = 'video/mp4'
      video.appendChild(desktopSource)
    }

    // Load and play the video
    video.load()
    this.playVideo()
  }

  playVideo() {
    if (!this.hasVideoTarget) return

    const video = this.videoTarget

    // Only play if sources have been added
    if (video.querySelectorAll('source').length === 0) return

    // Reset video to beginning
    video.currentTime = 0

    // Play the video
    const playPromise = video.play()

    if (playPromise !== undefined) {
      playPromise.catch(error => {
        // Auto-play was prevented, try again on user interaction
        console.log("Video autoplay prevented:", error)

        // Try to play on any user interaction
        const playOnInteraction = () => {
          video.play()
          document.removeEventListener("click", playOnInteraction)
          document.removeEventListener("touchstart", playOnInteraction)
        }

        document.addEventListener("click", playOnInteraction, { once: true })
        document.addEventListener("touchstart", playOnInteraction, { once: true })
      })
    }
  }
}
